import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { userService } from '../../services';
import Logo from '../../components/common/Logo';
import PageHeader from '../../components/common/PageHeader';
import { fileToBase64 } from '../../utils/fileUtils';

const CheckKycStatus = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        email: '',
        dob: '',
        citizenshipNumber: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [result, setResult] = useState(null);

    // Resubmission state
    const [showResubmitForm, setShowResubmitForm] = useState(false);
    const [resubmitData, setResubmitData] = useState({
        email: '',
        fullName: '',
        citizenshipNumber: '',
        citizenshipIssueDate: '',
        citizenshipIssueDistrict: '',
        dob: '',
        province: '',
        district: '',
        municipality: '',
        ward: '',
    });
    const [resubmitFiles, setResubmitFiles] = useState({
        front: null,
        back: null
    });
    const [isResubmitting, setIsResubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleResubmitChange = (e) => {
        const { name, value } = e.target;
        setResubmitData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            setResubmitFiles(prev => ({ ...prev, [type]: file }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.email || !formData.dob || !formData.citizenshipNumber) {
            setError('All fields are required');
            return;
        }

        setError('');
        setLoading(true);
        setResult(null);
        setShowResubmitForm(false);

        try {
            const response = await userService.checkKycStatus(formData);
            if (response.status) {
                setResult(response.data);
                // Pre-fill resubmission data
                setResubmitData({
                    email: response.data.email || formData.email,
                    fullName: response.data.fullName || '',
                    citizenshipNumber: formData.citizenshipNumber,
                    dob: formData.dob,
                    citizenshipIssueDate: '',
                    citizenshipIssueDistrict: '',
                    province: '',
                    district: '',
                    municipality: '',
                    ward: '',
                });
            } else {
                setError(response.message || 'Failed to check KYC status');
            }
        } catch (err) {
            console.error('Check KYC status error:', err);
            setError(err.response?.data?.message || 'Failed to check KYC status. Please check your details and try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResubmitKyc = async (e) => {
        e.preventDefault();
        setIsResubmitting(true);
        setError('');
        setSuccess('');

        try {
            // 1. Upload files first
            if (!resubmitFiles.front || !resubmitFiles.back) {
                throw new Error('Please upload both front and back images of your citizenship.');
            }

            const [frontBase64, backBase64] = await Promise.all([
                fileToBase64(resubmitFiles.front),
                fileToBase64(resubmitFiles.back)
            ]);

            const frontKey = frontBase64;
            const backKey = backBase64;

            if (!frontKey || !backKey) {
                console.error('Conversion failed - missing base64 data');
                throw new Error('File process failed. Please try again.');
            }

            // 2. Submit KYC data
            const payload = {
                ...resubmitData,
                citizenshipFrontKey: frontKey,
                citizenshipBackKey: backKey
            };

            await userService.resubmitKyc(payload);
            setSuccess('KYC resubmitted successfully! Our team will review it shortly.');
            setResult(prev => ({ ...prev, kycStatus: 'UNDER_REVIEW' }));
            setShowResubmitForm(false);

            // Re-fetch status to show updated history
            setTimeout(() => handleSubmit({ preventDefault: () => { } }), 3000);
        } catch (err) {
            console.error('Resubmission error:', err);
            setError(err.response?.data?.message || err.message || 'Failed to resubmit KYC. Please try again.');
        } finally {
            setIsResubmitting(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'VERIFIED':
                return <span className="px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-800">{t('kyc.statusVerified')}</span>;
            case 'UNDER_REVIEW':
                return <span className="px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">{t('kyc.statusUnderReview')}</span>;
            case 'REJECTED':
                return <span className="px-3 py-1 text-sm font-semibold rounded-full bg-red-100 text-red-800">{t('kyc.statusRejected')}</span>;
            case 'OPEN':
                return <span className="px-3 py-1 text-sm font-semibold rounded-full bg-gray-100 text-gray-800">{t('kyc.statusOpen')}</span>;
            default:
                return <span className="px-3 py-1 text-sm font-semibold rounded-full bg-yellow-100 text-yellow-800">{status}</span>;
        }
    };

    return (
        <div className="min-h-screen bg-primary-50 pb-12">
            <PageHeader
                title={t('kyc.checkKycStatus')}
                subtitle={t('kyc.checkStatusDescription')}
                icon={
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                }
            />
            <div className="max-w-4xl mx-auto px-4">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-8">

                        {!showResubmitForm && (
                            <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl mx-auto">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="col-span-1 md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('form.email')}
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            required
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                                            placeholder={t('kyc.enterEmail')}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('form.dateOfBirth')}
                                        </label>
                                        <input
                                            type="date"
                                            name="dob"
                                            required
                                            value={formData.dob}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('form.citizenshipNumber')}
                                        </label>
                                        <input
                                            type="text"
                                            name="citizenshipNumber"
                                            required
                                            value={formData.citizenshipNumber}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                                            placeholder={t('kyc.enterCitizenship')}
                                        />
                                    </div>
                                </div>

                                {error && !showResubmitForm && (
                                    <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm animate-pulse">
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-primary-600 text-white py-4 rounded-xl font-bold hover:bg-primary-700 transition transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 shadow-lg hover:shadow-xl"
                                >
                                    {loading ? (
                                        <span className="flex items-center justify-center">
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {t('kyc.checkingStatus')}
                                        </span>
                                    ) : t('kyc.checkMyStatus')}
                                </button>
                            </form>
                        )}

                        {showResubmitForm && (
                            <div className="max-w-2xl mx-auto animate-fadeIn">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-xl font-bold text-gray-900">{t('kyc.resubmitKyc')}</h3>
                                    <button
                                        onClick={() => setShowResubmitForm(false)}
                                        className="text-gray-500 hover:text-gray-700 text-sm font-medium"
                                    >
                                        {t('actions.cancel')}
                                    </button>
                                </div>

                                <form onSubmit={handleResubmitKyc} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="col-span-full">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fullName')}</label>
                                            <input
                                                type="text"
                                                name="fullName"
                                                required
                                                value={resubmitData.fullName}
                                                onChange={handleResubmitChange}
                                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.citizenshipNumber')}</label>
                                            <input
                                                type="text"
                                                name="citizenshipNumber"
                                                required
                                                value={resubmitData.citizenshipNumber}
                                                onChange={handleResubmitChange}
                                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.dateOfBirth')}</label>
                                            <input
                                                type="date"
                                                name="dob"
                                                required
                                                value={resubmitData.dob}
                                                onChange={handleResubmitChange}
                                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.citizenshipIssueDate')}</label>
                                            <input
                                                type="date"
                                                name="citizenshipIssueDate"
                                                required
                                                value={resubmitData.citizenshipIssueDate}
                                                onChange={handleResubmitChange}
                                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.citizenshipIssueDistrict')}</label>
                                            <input
                                                type="text"
                                                name="citizenshipIssueDistrict"
                                                required
                                                value={resubmitData.citizenshipIssueDistrict}
                                                onChange={handleResubmitChange}
                                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.province')}</label>
                                            <input
                                                type="text"
                                                name="province"
                                                required
                                                value={resubmitData.province}
                                                onChange={handleResubmitChange}
                                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.district')}</label>
                                            <input
                                                type="text"
                                                name="district"
                                                required
                                                value={resubmitData.district}
                                                onChange={handleResubmitChange}
                                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.municipality')}</label>
                                            <input
                                                type="text"
                                                name="municipality"
                                                required
                                                value={resubmitData.municipality}
                                                onChange={handleResubmitChange}
                                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.ward')}</label>
                                            <input
                                                type="text"
                                                name="ward"
                                                required
                                                value={resubmitData.ward}
                                                onChange={handleResubmitChange}
                                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.citizenshipFront')}</label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleFileChange(e, 'front')}
                                                required
                                                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.citizenshipBack')}</label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleFileChange(e, 'back')}
                                                required
                                                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                                            />
                                        </div>
                                    </div>

                                    {error && (
                                        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs italic">
                                            {error}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={isResubmitting}
                                        className="w-full bg-primary-600 text-white py-3 rounded-xl font-bold hover:bg-primary-700 transition disabled:opacity-50"
                                    >
                                        {isResubmitting ? t('kyc.submitting') : t('kyc.resubmitDocuments')}
                                    </button>
                                </form>
                            </div>
                        )}

                        <div className="mt-8 text-center">
                            <Link to="/" className="text-primary-600 font-medium hover:text-primary-700 flex items-center justify-center gap-1 group">
                                <svg className="w-4 h-4 transform group-hover:-translate-x-1 transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                {t('common.backToHome')}
                            </Link>
                        </div>
                    </div>

                    {result && !showResubmitForm && (
                        <div className="bg-primary-50 p-8 border-t border-primary-100 animate-fade-in">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {t('kyc.statusDetails')}
                                </h3>

                                {result.kycStatus === 'REJECTED' && (
                                    <button
                                        onClick={() => setShowResubmitForm(true)}
                                        className="bg-primary-600 text-white px-6 py-2 rounded-full font-bold hover:bg-primary-700 transition shadow-md flex items-center gap-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        {t('kyc.applyResubmission')}
                                    </button>
                                )}
                            </div>

                            {success && (
                                <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-800 rounded-xl text-sm font-medium animate-bounce text-center">
                                    {success}
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                                <div>
                                    <p className="text-sm text-gray-500 mb-1 font-medium">{t('form.fullName')}</p>
                                    <p className="text-lg font-bold text-gray-900">{result.fullName}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 mb-1 font-medium">{t('form.email')}</p>
                                    <p className="text-lg font-bold text-gray-900">{result.email}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 mb-1 font-medium">{t('kyc.currentStatus')}</p>
                                    <div className="mt-1">{getStatusBadge(result.kycStatus)}</div>
                                </div>
                                {result.rejectionReason && (
                                    <div className="col-span-full">
                                        <p className="text-sm text-red-500 mb-1 font-bold italic">{t('kyc.rejectionReason')}</p>
                                        <p className="p-4 bg-red-100 text-red-800 rounded-xl text-sm border border-red-200">
                                            {result.rejectionReason}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {(result.kycHistory || result.history) && (result.kycHistory || result.history).length > 0 && (
                                <div className="mt-10">
                                    <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">{t('kyc.verificationHistory')}</h4>
                                    <div className="space-y-4">
                                        {(result.kycHistory || result.history).slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((item, index) => (
                                            <div key={index} className="flex gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                                                <div className="flex-shrink-0 mt-1">
                                                    {item.status === 'REJECTED' ? (
                                                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                                                            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </div>
                                                    ) : (
                                                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                                                            <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-grow">
                                                    <div className="flex justify-between items-start mb-1">
                                                        <span className={`text-sm font-bold ${item.status === 'REJECTED' ? 'text-red-700' : 'text-primary-700'}`}>
                                                            {item.status}
                                                        </span>
                                                        <span className="text-xs text-gray-400">
                                                            {new Date(item.createdAt).toLocaleDateString(undefined, {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </span>
                                                    </div>
                                                    {item.reason && (
                                                        <p className="text-sm text-gray-600 leading-relaxed italic">
                                                            "{item.reason}"
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CheckKycStatus;
